<template>
  <IonHeader ref="headerRef" class="ion-no-border bg-white" id="app-header">
    <IonToolbar color="white" :class="isWeb ? 'web' : ''">
      <IonButtons class="search-transition" :class="searchOpen ? 'active' : ''" slot="start">
        <IonMenuButton
          @click="() => (searchOpen = false)"
          :class="searchOpen ? '' : 'border-r-2 border-gray-500/20'"
          class="px-1"
          color="dark"
        ></IonMenuButton>
      </IonButtons>
      <SearchBar
        v-model="searchText"
        class="search-transition ml-2"
        :class="searchOpen ? 'active' : ''"
        ref="searchInputRef"
      ></SearchBar>
      <IonButtons slot="end" v-show="!searchOpen">
        <HelpGuideButton></HelpGuideButton>
        <ProfileButton class="border-l-2 border-gray-500/20"></ProfileButton>
      </IonButtons>
      <IonButtons slot="end" v-show="searchOpen">
        <BarcodeScannerButton @on-scan="onBarcodeScan($event)"></BarcodeScannerButton>
        <IconButton @click="clear()" class="border-l-2 border-gray-500/20" icon="icon-x" color="dark"></IconButton>
      </IonButtons>
    </IonToolbar>
  </IonHeader>
  <Teleport to="#app">
    <Transition name="fade" mode="out-in">
      <SearchResults
        :search-text="searchText"
        :top-offset="headerHeight || 56"
        v-if="searchResults?.length"
        :search-results="searchResults"
        @device-result-clicked="onDeviceResultClicked($event)"
        @property-result-clicked="onPropertyResultClicked($event)"
        @dismiss="ontapDismiss()"
      ></SearchResults>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import {
  createAnimation,
  IonButtons,
  IonHeader,
  IonMenuButton,
  IonToolbar,
  isPlatform,
  useIonRouter,
} from '@ionic/vue';
import { computed, ref, watch, onBeforeUnmount } from 'vue';
import IconButton from '../forms/IconButton.vue';
import SearchBar from './search/SearchBar.vue';
import { SearchService } from '@/http-services/search.service';
import { onClickOutside, useElementBounding, watchDebounced } from '@vueuse/core';
import SearchResults from './search/SearchResults.vue';
import { ProgrammedDeviceContext } from 'common';
import BarcodeScannerButton from '../BarcodeScannerButton.vue';
import { DeviceFamily, DeviceTypeHelper } from '@ncss/models';
import HelpGuideButton from './HelpGuideButton.vue';
import ProfileButton from '@/components/menu/ProfileButton.vue';

const isWeb = computed(() => isPlatform('desktop') || isPlatform('mobileweb'));
const searchInputRef = ref<InstanceType<typeof SearchBar>>();
const searchOpen = ref(false);
const headerRef = ref<InstanceType<typeof IonHeader>>();
const { height: headerHeight } = useElementBounding(() => headerRef.value?.$el);
onClickOutside(headerRef, () => {
  searchOpen.value = false;
});
const searchText = ref<string>();
const searchService = new SearchService();
const searchResults = ref<any[]>([]);
const router = useIonRouter();
let abortController: AbortController | null = null;

watchDebounced(
  searchText,
  (txt) => {
    if (txt && txt.length >= 3) {
      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();
      searchService.search(txt, abortController.signal).then((results) => {
        if (searchText.value === txt) {
          searchResults.value = results;
        }
      });
    } else {
      searchResults.value = [];
    }
  },
  { debounce: 200, immediate: true }
);

onBeforeUnmount(() => {
  if (abortController) {
    abortController.abort();
  }
});

watch(
  () => searchInputRef.value?.isFocused,
  (focused) => {
    if (focused) {
      searchOpen.value = true;
    }
  }
);

function clear() {
  searchInputRef.value?.clear();
  searchOpen.value = false;
}

function onBarcodeScan(scanned: number) {
  searchText.value = scanned?.toString(16).toUpperCase();
}

function ontapDismiss() {
  searchText.value = undefined;
}

function onPropertyResultClicked(propertyId: string) {
  searchText.value = undefined;
  return router.push(`/property/${propertyId}`);
}

function onDeviceResultClicked(val: { context?: ProgrammedDeviceContext; deviceId?: number }) {
  if (val.context && val.deviceId) {
    searchText.value = undefined;
    const deviceType = DeviceTypeHelper.GetDeviceTypeBySerialNumber(val.deviceId);
    if (val.context.unit) {
      return router.push({
        path: `/unit/${val.context.property.id}/${val.context.unit.id}`,
        query: { deviceId: val.deviceId },
      });
    } else if (val.context.property && deviceType?.family === DeviceFamily.GATEWAY) {
      return router.push(`/gateway/${val.context.property.id}`);
    } else if (
      val.context.property &&
      (deviceType?.family === DeviceFamily.REPEATER || deviceType?.family === DeviceFamily.REPEATER_3RD_PARTY)
    ) {
      return router.push(`/repeater/${val.context.property.id}/${val.deviceId}`);
    } else if (val.context.property) {
      return router.push(`/property/${val.context.property.id}`);
    }
  }
}
</script>

<style scoped>
.search-transition {
  transition: all 0.2s ease;
}
.search-transition.active {
  transform: translateX(-54px);
}
ion-header {
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
}
ion-toolbar.web {
  @apply min-h-14 flex items-center;
}
</style>
